// The tutorial videos: one registry, read by everything that shows one.
//
// docs/capture/publish.mjs writes public/guides/videos.json from the video
// scripts and the assembled cuts — id, slug, title, summary, chapters,
// duration, poster — and this module is the only place the app reads it. The
// guide pages' embedded players, the /videos watch pages, the hub and the
// tools' help buttons all come through here, so a new video is a content
// change in docs/ and nothing in src/ has to know.
//
// Three names for one video, on purpose (see the design spec, §5.3):
//   id     stable, what application code refers to     'nmonteG25'
//   slug   public, the watch-page URL                   /videos/nmonte-g25
//   media  the files on disk and the guide's figure     assets/video/nmonte-g25.mp4
// resolveVideo() accepts any of them, so a caller never has to know which.
//
// Versioning is the guides' own scheme: the files keep their names forever,
// so the URL carries a version from cacheVer (see guides.vue for why), and
// that logic lives here so the guides page and the video pages can't drift.
import axios from 'axios'

export const GUIDES_BASE = import.meta.env.VITE_GUIDES_BASE || '/guides'

// Until cacheVer carries a `guides` key the fallback busts on every load,
// which is correct if uncached; it never serves stale content.
const CACHE_QUERY = '?v=0.'
export function guidesVersion() {
    try {
        return JSON.parse(localStorage.getItem('cacheVer') || '{}').guides || Date.now()
    } catch {
        return Date.now()
    }
}
export const versioned = url => `${url}${CACHE_QUERY + guidesVersion()}`

/** A guide asset ('assets/video/pca.mp4') as the versioned URL the app fetches. */
export const guideAsset = rel => versioned(`${GUIDES_BASE}/${rel}`)

/** The same asset as a stable absolute URL, for structured data and og: tags.
 *  Unversioned on purpose: a crawler should see one URL for a video, not one
 *  per deploy. */
export const guideAssetUrl = rel => `${siteOrigin()}${GUIDES_BASE}/${rel}`

export const siteOrigin = () =>
    (typeof window !== 'undefined' && window.location?.origin) || 'https://genoplot.com'

export const watchPath = v => (typeof v === 'string' ? `/videos/${v}` : (v?.watchPath || `/videos/${v?.slug}`))
export const watchUrl = v => `${siteOrigin()}${watchPath(v)}`
export const youtubeEmbedUrl = (youtubeId, start = 0) =>
    `https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0${start ? `&start=${Math.floor(start)}` : ''}`

// The guides' own content styles are generated from docs.css by
// capture/publish.mjs and already scoped to .guides-root, so they are injected
// once and shared by every documentation page.
export function ensureGuideStyles() {
    if (typeof document === 'undefined') return
    const id = 'guides-content-css'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = guideAsset('guide-content.css')
    document.head.appendChild(link)
}

// One in-flight request however many components ask. A failed fetch clears the
// cache so the next caller retries rather than inheriting the failure.
let videosPending = null
export function loadGuideVideos() {
    if (!videosPending) {
        videosPending = axios.get(guideAsset('videos.json'))
            .then(r => (Array.isArray(r.data) ? r.data : []))
            .catch(() => { videosPending = null; return [] })
    }
    return videosPending
}

let indexPending = null
export function loadGuidesIndex() {
    if (!indexPending) {
        indexPending = axios.get(guideAsset('index.json'))
            .then(r => (Array.isArray(r.data) ? r.data : (r.data?.guides || [])))
            .catch(() => { indexPending = null; return [] })
    }
    return indexPending
}

/** Find a video by id, slug or media key — whichever the caller has. */
export function findVideo(videos, ref) {
    if (!ref) return null
    return videos.find(v => v.id === ref) || videos.find(v => v.slug === ref) || videos.find(v => v.media === ref) || null
}
export async function resolveVideo(ref) {
    return findVideo(await loadGuideVideos(), ref)
}
/** By public slug only — what the watch route has. */
export async function guideVideoBySlug(slug) {
    return (await loadGuideVideos()).find(v => v.slug === slug) || null
}
export async function videosForGuide(guideSlug) {
    return (await loadGuideVideos()).filter(v => v.guide === guideSlug)
}
export async function indexableVideos() {
    return (await loadGuideVideos()).filter(v => v.indexable !== false)
}

/** The start of a chapter, by title (case-insensitive, whitespace-loose), so a
 *  help control can name the section it means rather than a number that
 *  moves on every re-render of the narration. */
export function chapterStart(video, title) {
    if (!video || !title) return 0
    const norm = s => String(s).toLowerCase().replace(/\s+/g, ' ').trim()
    const want = norm(title)
    const c = (video.chapters || []).find(c => norm(c.title) === want)
        || (video.chapters || []).find(c => norm(c.title).startsWith(want))
    return c ? c.start : 0
}

/** 169 → "2:49" */
export function formatClock(seconds) {
    const s = Math.max(0, Math.round(Number(seconds) || 0))
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** "90", "1:30", "1m30s", "90s" → 90. Anything else → 0. */
export function parseTime(value) {
    const t = String(value ?? '').trim().toLowerCase()
    if (!t) return 0
    if (/^\d+(\.\d+)?$/.test(t)) return Math.floor(Number(t))
    const clock = /^(?:(\d+):)?(\d+):(\d\d)$/.exec(t)
    if (clock) return Number(clock[1] || 0) * 3600 + Number(clock[2]) * 60 + Number(clock[3])
    const units = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(t)
    if (units && (units[1] || units[2] || units[3])) {
        return Number(units[1] || 0) * 3600 + Number(units[2] || 0) * 60 + Number(units[3] || 0)
    }
    return 0
}

// The captions double as the transcript. Parsing them here means the watch
// page carries the whole narration as text — the part of the page a crawler
// can actually read — without publish.mjs writing the same words out twice.
const stamp = s => {
    const m = /(?:(\d+):)?(\d+):(\d\d)[.,](\d{1,3})/.exec(s)
    if (!m) return 0
    return Number(m[1] || 0) * 3600 + Number(m[2]) * 60 + Number(m[3]) + Number(m[4].padEnd(3, '0')) / 1000
}
export function parseVtt(text) {
    const cues = []
    for (const block of String(text || '').replace(/\r/g, '').split(/\n\n+/)) {
        const lines = block.split('\n').filter(Boolean)
        const i = lines.findIndex(l => l.includes('-->'))
        if (i < 0) continue
        const [from, to] = lines[i].split('-->')
        const body = lines.slice(i + 1).join(' ').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()
        if (!body) continue
        cues.push({ start: stamp(from), end: stamp(to), text: body })
    }
    return cues
}

/** Group cues under the video's chapters, so the transcript reads as sections
 *  with the same headings as the chapter list. Cues before the first chapter
 *  (the cold open) come first, untitled. */
export function transcriptSections(cues, chapters = []) {
    const marks = [...(chapters || [])].sort((a, b) => a.start - b.start)
    const sections = []
    const push = (title, start, text) => { if (text) sections.push({ title, start, text }) }
    if (!marks.length) { push(null, 0, cues.map(c => c.text).join(' ')); return sections }
    push(null, 0, cues.filter(c => c.start < marks[0].start).map(c => c.text).join(' '))
    marks.forEach((m, i) => {
        const next = marks[i + 1]?.start ?? Infinity
        push(m.title, m.start, cues.filter(c => c.start >= m.start && c.start < next).map(c => c.text).join(' '))
    })
    return sections
}

/** The VideoObject for a watch page. Everything Google requires (name,
 *  description, thumbnailUrl, uploadDate) plus the fields that earn key
 *  moments (hasPart Clips with seekable URLs) and a readable transcript. */
export function videoJsonLd(v, { transcript } = {}) {
    const url = watchUrl(v)
    const clips = (v.chapters || []).map(c => ({
        '@type': 'Clip',
        name: c.title,
        startOffset: c.start,
        ...(c.end != null ? { endOffset: c.end } : {}),
        url: `${url}?t=${c.start}`,
    }))
    return {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        '@id': `${url}#video`,
        name: v.title,
        description: v.summary || v.name,
        ...(v.poster ? { thumbnailUrl: [guideAssetUrl(v.poster)] } : {}),
        ...(v.src ? { contentUrl: guideAssetUrl(v.src) } : {}),
        ...(v.youtubeId ? { embedUrl: youtubeEmbedUrl(v.youtubeId) } : {}),
        url,
        inLanguage: 'en',
        isFamilyFriendly: true,
        ...(v.duration ? { duration: v.duration } : {}),
        ...(v.uploadDate ? { uploadDate: v.uploadDate } : {}),
        ...(v.width ? { width: v.width, height: v.height } : {}),
        ...(clips.length ? { hasPart: clips } : {}),
        ...(transcript ? { transcript } : {}),
        publisher: {
            '@type': 'Organization',
            name: 'Genoplot',
            url: siteOrigin(),
        },
    }
}

// ── Instrumentation ─────────────────────────────────────────────────────────
// Through the app's own $gtag (a no-op shim when analytics is off), never a
// second pipeline. Events: video_help_open, video_play, video_chapter_click,
// video_full_tutorial_click, video_watch_page_view, video_related_guide_click.
export function trackVideo(vm, event, video, extra = {}) {
    try {
        vm?.$gtag?.event?.(event, {
            event_category: 'video',
            videoId: video?.id,
            videoSlug: video?.slug,
            route: vm?.$route?.path,
            ...extra,
        })
    } catch { /* analytics must never break a page */ }
}

// ── Contextual help ─────────────────────────────────────────────────────────
// The one way to open a tutorial from inside a tool. There is a single
// GuideVideoModal mounted in App.vue, so however many help controls a page
// carries there is one player. Pass an id (preferred), a slug or a media key,
// and either a chapter title or a start in seconds.
export function openVideoHelp(vm, { id, slug, videoId, chapter, start = 0, context = null } = {}) {
    const ref = videoId || id || slug
    vm.$modal.show('GuideVideoModal', { ref, chapter, start, context })
    trackVideo(vm, 'video_help_open', { id: ref, slug }, { context, startSeconds: start, chapter })
}
